import React from 'react';
import { FileText, Scale, AlertCircle, DollarSign, Wrench, Shield, ArrowRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const TermsConditionsPage = () => {
  const sections = [
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Acceptance of Terms",
      content: [
        {
          text: "By accessing and using the services of GUS Group Ltd, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. These terms apply to all visitors, users, and others who access or use our services."
        },
        {
          text: "If you do not agree with any part of these terms, you must not use our website or engage our services. We reserve the right to modify these terms at any time, and such modifications will be effective immediately upon posting on our website."
        }
      ]
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: "Services Provided",
      content: [
        {
          subtitle: "Construction Services",
          text: "GUS Group Ltd provides comprehensive construction and real estate services including but not limited to building development, remodeling, renovations, road construction, interior and exterior decoration, and related services as described on our website."
        },
        {
          subtitle: "Service Scope",
          text: "The specific scope of services for each project will be defined in a separate written agreement or contract. All services are subject to availability and our capacity to undertake the work."
        },
        {
          subtitle: "Professional Standards",
          text: "We commit to providing all services in accordance with industry standards, applicable building codes, and regulations in Ghana. All work will be performed by qualified professionals."
        }
      ]
    },
    {
      icon: <DollarSign className="w-8 h-8" />,
      title: "Pricing and Payment",
      content: [
        {
          subtitle: "Quotations",
          text: "All quotations provided are estimates based on the information available at the time. Final pricing will be confirmed in the project contract and may be subject to adjustment based on actual project requirements, material costs, or unforeseen circumstances."
        },
        {
          subtitle: "Payment Terms",
          text: "Unless otherwise agreed in writing, our standard payment terms are: 40% deposit upon contract signing, 30% upon design approval or project commencement, 20% at 50% project completion, and 10% upon final completion and handover."
        },
        {
          subtitle: "Late Payments",
          text: "Late payments may be subject to interest charges at the rate of 2% per month or the maximum rate permitted by law, whichever is lower. We reserve the right to suspend work on projects with outstanding payments."
        },
        {
          subtitle: "Additional Costs",
          text: "Any changes to the original scope of work requested by the client may result in additional charges. Such changes must be agreed upon in writing before implementation."
        }
      ]
    },
    {
      icon: <Scale className="w-8 h-8" />,
      title: "Client Responsibilities",
      content: [
        {
          subtitle: "Information Accuracy",
          text: "Clients must provide accurate and complete information regarding project requirements, site conditions, and any relevant circumstances that may affect the project."
        },
        {
          subtitle: "Site Access",
          text: "Clients are responsible for ensuring that we have appropriate access to the project site during agreed working hours and that the site is prepared as specified in the project agreement."
        },
        {
          subtitle: "Permits and Approvals",
          text: "Unless specifically agreed otherwise in writing, clients are responsible for obtaining necessary permits, approvals, and permissions from relevant authorities before project commencement."
        },
        {
          subtitle: "Decision Making",
          text: "Clients must make timely decisions on matters requiring their input to avoid project delays. Delays caused by client indecision may result in timeline adjustments and potential additional costs."
        }
      ]
    },
    {
      icon: <AlertCircle className="w-8 h-8" />,
      title: "Project Timeline and Delays",
      content: [
        {
          subtitle: "Timeline Estimates",
          text: "Project timelines provided are estimates based on normal working conditions and the information available at the time of quotation. Actual completion dates may vary due to factors beyond our control."
        },
        {
          subtitle: "Force Majeure",
          text: "We shall not be liable for any delays or failure to perform our obligations due to circumstances beyond our reasonable control, including but not limited to natural disasters, weather conditions, strikes, material shortages, or government actions."
        },
        {
          subtitle: "Client-Caused Delays",
          text: "Delays caused by client actions or inactions, including late payments, delayed decisions, or failure to provide required information, may result in timeline extensions and potential additional costs."
        }
      ]
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Warranties and Guarantees",
      content: [
        {
          subtitle: "Workmanship Warranty",
          text: "We warrant that all work will be performed in a professional and workmanlike manner in accordance with industry standards. Defects in workmanship reported within 12 months of project completion will be remedied at no additional cost to the client."
        },
        {
          subtitle: "Material Warranties",
          text: "Materials used in our projects are covered by manufacturer warranties where applicable. We will assist clients in claiming under such warranties but are not responsible for defects in materials beyond our control."
        },
        {
          subtitle: "Exclusions",
          text: "Warranties do not cover damage caused by misuse, lack of proper maintenance, unauthorized modifications, or normal wear and tear. Warranties are void if clients engage third parties to modify or repair our work without our written consent."
        }
      ]
    }
  ];

  const additionalSections = [
    {
      title: "Intellectual Property",
      content: "All designs, plans, drawings, and other intellectual property created by GUS Group Ltd in the course of providing services remain our property unless explicitly transferred to the client in writing. Clients may not reproduce, distribute, or use such materials for purposes other than the specific project for which they were created without our written permission."
    },
    {
      title: "Confidentiality",
      content: "Both parties agree to maintain the confidentiality of any proprietary or sensitive information disclosed during the course of the business relationship. This obligation continues even after the completion of services or termination of the agreement."
    },
    {
      title: "Limitation of Liability",
      content: "To the maximum extent permitted by law, GUS Group Ltd's total liability for any claims arising from our services shall not exceed the total amount paid by the client for the specific project in question. We shall not be liable for any indirect, incidental, special, or consequential damages."
    },
    {
      title: "Insurance",
      content: "GUS Group Ltd maintains appropriate insurance coverage for our operations. Clients are responsible for maintaining their own insurance coverage for their property and projects as they deem appropriate."
    },
    {
      title: "Termination",
      content: "Either party may terminate a project agreement by providing written notice as specified in the project contract. Upon termination, the client shall pay for all work completed and materials purchased up to the date of termination, plus any applicable termination fees as specified in the contract."
    },
    {
      title: "Dispute Resolution",
      content: "In the event of any dispute arising from these terms or our services, the parties agree to first attempt to resolve the matter through good faith negotiation. If negotiation fails, disputes shall be resolved through mediation or arbitration in Accra, Ghana, in accordance with Ghanaian law."
    },
    {
      title: "Governing Law",
      content: "These Terms and Conditions shall be governed by and construed in accordance with the laws of the Republic of Ghana. Any legal actions or proceedings arising from these terms shall be brought exclusively in the courts of Ghana."
    },
    {
      title: "Severability",
      content: "If any provision of these Terms and Conditions is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect. The invalid provision shall be modified to the minimum extent necessary to make it valid and enforceable."
    },
    {
      title: "Entire Agreement",
      content: "These Terms and Conditions, together with any project-specific contracts or agreements, constitute the entire agreement between GUS Group Ltd and the client regarding our services. No other representations, warranties, or agreements shall be binding unless made in writing and signed by both parties."
    }
  ];

  return (
    <div className="bg-black text-white">
      <Navigation onQuoteClick={() => window.location.href = '/contact'} />

      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-400 rounded-full filter blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-600 rounded-full filter blur-3xl"></div>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="w-20 h-20 bg-yellow-400/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Scale className="w-10 h-10 text-yellow-400" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Terms & Conditions
          </h1>
          <p className="text-xl text-gray-300 mb-4 max-w-3xl mx-auto">
            Please read these terms carefully before using our services
          </p>
          <p className="text-sm text-gray-400">
            Last Updated: January 20, 2026
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-yellow-400/10 to-yellow-600/10 border border-yellow-400/30 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">Welcome to GUS Group Ltd</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              These Terms and Conditions ("Terms") govern your use of GUS Group Ltd's services and website. By engaging our services or using our website, you enter into a legally binding agreement with us.
            </p>
            <p className="text-gray-300 leading-relaxed">
              Please read these Terms carefully. Your continued use of our services constitutes acceptance of these Terms. If you have any questions or concerns about these Terms, please contact us before proceeding.
            </p>
          </div>
        </div>
      </section>

      {/* Main Sections */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {sections.map((section, index) => (
            <div key={index} className="scroll-mt-24">
              <div className="flex items-start gap-6 mb-8">
                <div className="w-16 h-16 bg-yellow-400/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <div className="text-yellow-400">
                    {section.icon}
                  </div>
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-white mb-2">
                    {section.title}
                  </h2>
                  <div className="w-20 h-1 bg-yellow-400"></div>
                </div>
              </div>

              <div className="space-y-6">
                {section.content.map((item, idx) => (
                  <div key={idx} className="bg-gradient-to-br from-gray-900 to-black p-6 rounded-xl border border-yellow-400/10">
                    {item.subtitle && (
                      <h3 className="text-xl font-bold text-yellow-400 mb-3">
                        {item.subtitle}
                      </h3>
                    )}
                    <p className="text-gray-300 leading-relaxed">
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Additional Sections */}
      <section className="py-24 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {additionalSections.map((section, index) => (
              <div key={index}>
                <h2 className="text-3xl font-bold text-white mb-6">{section.title}</h2>
                <div className="bg-gradient-to-br from-gray-900 to-black p-6 rounded-xl border border-yellow-400/10">
                  <p className="text-gray-300 leading-relaxed">
                    {section.content}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-yellow-400/10 to-yellow-600/10 border border-yellow-400/30 rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Questions About These Terms?</h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              If you have any questions about these Terms and Conditions, please don't hesitate to contact us.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-black/50 p-6 rounded-xl">
                <FileText className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
                <h3 className="text-white font-bold mb-2">Email</h3>
                <a href="mailto:legal@gusgroupltd.com" className="text-yellow-400 hover:text-yellow-500 transition-colors">
                  legal@gusgroupltd.com
                </a>
              </div>

              <div className="bg-black/50 p-6 rounded-xl">
                <Scale className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
                <h3 className="text-white font-bold mb-2">Phone</h3>
                <a href="tel:0592668258" className="text-yellow-400 hover:text-yellow-500 transition-colors">
                  0592668258
                </a>
              </div>

              <div className="bg-black/50 p-6 rounded-xl">
                <Shield className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
                <h3 className="text-white font-bold mb-2">Office</h3>
                <p className="text-gray-400 text-sm">
                  Nii Afutu Tsuru St<br />Accra, Ghana
                </p>
              </div>
            </div>

            
            <a  href="/contact"
              className="inline-flex items-center gap-2 bg-yellow-400 text-black px-8 py-3 rounded-full font-bold hover:bg-yellow-500 transition-all transform hover:scale-105"
            >
              Contact Us
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* WhatsApp Floating Button */}
      
      <a  href="https://wa.me/233592668258?text=Hello%20GUS%20Group,%20I%20have%20a%20question%20about%20your%20terms"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-8 right-8 w-16 h-16 bg-green-500 rounded-full flex items-center justify-center shadow-2xl hover:bg-green-600 transition-all transform hover:scale-110 z-40"
      >
        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>
    </div>
  );
};

export default TermsConditionsPage;